"use client";
import cx from "classnames";
import React from "react";
import { withQuery } from "with-query";

import Table, { type TableColumnsT, type BuildDataUrlT } from "@/PartTable";
import { Table2Ref } from "@/PartTable/PartTable";

import styles from "./AddressImportTable.module.scss";

/**
 * Import address row from GET /api/v1/import-tasks/{id}/addresses
 * Matches ImportAddressResponse from backend
 */
type ImportItemT = {
  id: number;
  row_number: number | null;
  address: {
    city: string;
    street_full_name: string;
    street_number: string;
    postal_code?: string;
    unit?: string;
  } | null;
  status?: string | null;
  import_error?: string | null;
};

interface AddressImportTableProps {
  title?: string;
  subTitle?: string;
  className?: string;
  importTaskId: string;
  importIsRunning: boolean;
}

const AddressImportTable: React.FC<AddressImportTableProps> = props => {
  const { title, subTitle, className, importTaskId, importIsRunning } = props;

  const tableRef = React.useRef<Table2Ref | null>(null);

  // Refresh table once when import completes (isRunning transitions from true to false)
  const prevIsRunning = React.useRef(importIsRunning);
  React.useEffect(() => {
    if (prevIsRunning.current && !importIsRunning) {
      // Import just completed - refresh to show final results
      tableRef.current?.refresh();
    }
    prevIsRunning.current = importIsRunning;
  }, [importIsRunning]);

  const buildDataUrl = React.useCallback<BuildDataUrlT<ImportItemT>>(
    ({ page = 1, size = 10 }) => {
      return withQuery(
        process.env.NEXT_PUBLIC_API_URL +
          `/api/v1/import-tasks/${importTaskId}/addresses`,
        {
          skip: (page - 1) * size,
          limit: size,
          sort: "row_number",
        },
      );
    },
    [importTaskId],
  );

  const columns = React.useMemo<TableColumnsT<ImportItemT>>(
    () => [
      {
        label: "#",
        maxWidth: 100,
        renderCell: item => !("_dummy" in item) && (item?.row_number as number),
      },
      {
        label: "Address",
        maxWidth: 480,
        renderCell: item => {
          if ("_dummy" in item) return;
          if (typeof item?.address === "string") return item?.address;
          if (typeof item?.address === "object") {
            return [
              item?.address?.street_number,
              item?.address?.street_full_name,
              item?.address?.city,
              item?.address?.postal_code,
            ]
              .filter(it => !!it)
              .map(it => String(it).trim())
              .join(", ");
          }
        },
      },
      {
        label: "Error",
        renderCell: item => {
          if ("_dummy" in item) return null;

          return (
            <div className={styles.tableRowStatusAndActionButtons}>
              <div className={styles.error}>
                <p>{item?.import_error}</p>
              </div>
            </div>
          );
        },
      },
    ],
    [],
  );

  return (
    <div className={cx(styles.root, className)}>
      <div className={styles.title}>
        <span>{title}</span>
      </div>
      <div className={styles.subTitle}>
        <span>{subTitle}</span>
      </div>
      <Table<ImportItemT>
        tableName="addressImportTable"
        buildDataUrl={buildDataUrl}
        columns={columns}
        className={styles.table}
        rowsPerPage={10}
        showLoaderOnlyOnce={importIsRunning}
        ref={tableRef}
      />
    </div>
  );
};

export default AddressImportTable;
